import { FileText, Calendar, Truck, CheckCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export const HowItWorks = () => {
  const steps = [
    {
      number: '01',
      icon: FileText,
      title: 'Request a Quote',
      description: 'Fill out our simple form with your shipment details, pickup and delivery locations, and preferred timing.'
    },
    {
      number: '02',
      icon: Calendar,
      title: 'Schedule Pickup',
      description: 'Choose a convenient time for pickup. We offer flexible scheduling to match your business operations.'
    },
    {
      number: '03',
      icon: Truck,
      title: 'Secure Transport',
      description: 'Our professional drivers collect and transport your freight with care. Track in real-time via our system.'
    },
    {
      number: '04',
      icon: CheckCircle,
      title: 'On-Time Delivery',
      description: 'Your shipment arrives safely and on schedule. Receive delivery confirmation with signature proof.'
    }
  ];

  return (
    <section id="how-it-works" className="py-24 bg-muted/30 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute bottom-1/4 left-0 w-[500px] h-[500px] bg-secondary/5 blur-[120px] rounded-full" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 scroll-animate">
          <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10">
            Our Process
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            How <span className="text-gradient">It Works</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Simple, transparent, and efficient. Get your shipments delivered in four easy steps.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Connection line for desktop */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/20 via-primary to-primary/20" />
          
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div 
                key={index} 
                className="scroll-animate relative"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <Card className="p-6 bg-card/80 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-glow group h-full flex flex-col">
                  {/* Step number */}
                  <div className="relative mb-6">
                    <div className="absolute -top-8 -left-8 text-7xl font-bold text-primary/10 group-hover:text-primary/20 transition-colors">
                      {step.number}
                    </div>
                    <div className="relative z-10 w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center group-hover:shadow-glow transition-all duration-300">
                      <Icon className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {step.description}
                  </p>
                </Card>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-16 scroll-animate">
          <p className="text-lg text-muted-foreground mb-4">
            Ready to experience seamless logistics?
          </p>
          <button 
            onClick={() => {
              const element = document.getElementById('quote');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-semibold group"
          >
            Get Started Now
            <span className="group-hover:translate-x-1 transition-transform">→</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
